import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface RecentRecipient {
  name: string;
  email: string;
}

/**
 * Recipients the workspace has previously sent envelopes to, most-recently-
 * used first. Feeds the autofill suggestions in the builder's recipient
 * rows. Idle until `holderId` resolves so we don't fire an unkeyed query.
 *
 * Stale-time is long-ish (5 min) — recipient history doesn't move fast, and
 * the suggestions don't have to be perfectly fresh for the UX to feel right.
 */
export function useRecentRecipients(holderId: string | null): {
  recipients: RecentRecipient[];
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: holderId ? queryKeys.documentSign.recentRecipients(holderId) : ['__idle__'],
    queryFn: () => api.listRecentDocumentSignRecipients({ holderId: holderId!, limit: 20 }),
    enabled: !!holderId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
  return {
    recipients: (data?.recipients ?? []).map((r) => ({ name: r.name, email: r.email })),
    isLoading,
  };
}
